import {
  buildTemplateTasks,
  cleanTaskLabel,
  getQualificationTaskProposals,
  hasPendingTasks,
  normalizeTaskLabel,
  withQualificationTasks,
} from 'components/SlideOver/dataSlideOver/data/slideOverAddMission/missionTemplate/templateFormData';
import { missionDetailText } from './missionDetailTexts';

/** Twin of the api limit on `tasks` in updateMissionDetails.usecase.js. */
export const MISSION_TASKS_MAX_COUNT = 50;

const toTaskLabels = (values) =>
  (Array.isArray(values) ? values : [])
    .filter((label) => typeof label === 'string')
    .map(cleanTaskLabel)
    .filter(Boolean);

// The form never lists one task twice: a duplicate stored on an old mission
// would otherwise read as a change the admin never made.
const dedupeTaskLabels = (labels) => {
  const seen = new Set();
  return labels.filter((label) => {
    const key = normalizeTaskLabel(label);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** The tasks stored on the mission itself; none when it relies on its qualification. */
export const ownMissionTasks = (mission) => toTaskLabels(mission?.tasks);

/**
 * The tasks the mission shows today: its own, otherwise the ones the api
 * resolves from its qualification (`missionTasks`).
 */
const currentMissionTasks = (mission) => {
  const own = ownMissionTasks(mission);
  return dedupeTaskLabels(
    own.length > 0 ? own : toTaskLabels(mission?.missionTasks)
  );
};

/**
 * The tasks form of the job description wizard, filled from a mission: the
 * tasks of `qualification` are proposed, ticked when the mission has them
 * (all of them when it has no tasks of its own), and the mission's other
 * tasks are listed as added ones.
 */
export const missionTaskFormOf = (mission, qualification, qualificationName) => {
  const proposals = getQualificationTaskProposals(qualification);
  const proposalByKey = new Map(
    proposals.map((proposal) => [normalizeTaskLabel(proposal.label), proposal])
  );
  const matched = [];
  const added = [];
  currentMissionTasks(mission).forEach((label) => {
    const proposal = proposalByKey.get(normalizeTaskLabel(label));
    if (proposal) {
      // Stored with the proposal's id so the row keeps the mission's wording.
      matched.push({ qualificationTaskId: proposal.qualificationTaskId, label });
    } else {
      added.push(label);
    }
  });
  return {
    qualificationLabel: qualificationName || '',
    taskProposals: proposals,
    templateQualificationTasks: matched,
    taskChecks: {},
    taskProposalsCheckedByDefault: ownMissionTasks(mission).length === 0,
    companyTasks: added,
    pendingTasks: [],
    taskNotice: null,
  };
};

/**
 * The tasks form for the qualification chosen in the edit form. Null until the
 * qualification referential is loaded. Back on the mission's own qualification
 * the form is the mission's again; another qualification behaves as in the
 * wizard: its tasks all ticked, the added tasks kept.
 */
export const syncMissionTaskForm = (
  taskForm,
  mission,
  qualificationName,
  qualifications
) => {
  if (!qualificationName || !qualifications?.length) return taskForm ?? null;
  if (taskForm && taskForm.qualificationLabel === qualificationName) {
    return taskForm;
  }
  const qualification = qualifications.find(
    (item) => item?.name === qualificationName
  );
  if (!taskForm || qualificationName === mission?.title) {
    return missionTaskFormOf(mission, qualification, qualificationName);
  }
  return {
    ...withQualificationTasks(taskForm, qualification),
    qualificationLabel: qualificationName,
  };
};

/** The labels the mission would keep, in the order the form shows them. */
export const missionTasksAfter = (taskForm) =>
  buildTemplateTasks(taskForm).map((task) => task.label);

// Order is left out: the form cannot reorder tasks, and a mission whose stored
// order differs from the displayed one must not count as changed on opening.
const sameTaskLabels = (a, b) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((label, index) => label === sortedB[index]);
};

/** The `tasks` change of the edit form, or null. */
export const missionTasksChange = (mission, taskForm) => {
  if (!taskForm) return null;
  const after = missionTasksAfter(taskForm);
  const before = currentMissionTasks(mission);
  if (sameTaskLabels(after, before)) return null;
  // Switching to a qualification without active tasks leaves nothing to keep:
  // the mission keeps its tasks (the api refuses an empty list anyway).
  if (
    after.length === 0 &&
    taskForm.qualificationLabel !== (mission?.title || '')
  ) {
    return null;
  }
  return { field: 'tasks', before, after };
};

/**
 * Why the tasks cannot be saved, or null. A task still being checked blocks
 * any save; the count only matters to a list being changed.
 */
export const missionTasksError = (mission, taskForm) => {
  if (!taskForm) return null;
  if (hasPendingTasks(taskForm)) return missionDetailText('tasks.pending');
  const change = missionTasksChange(mission, taskForm);
  if (!change) return null;
  if (change.after.length === 0) return missionDetailText('tasks.required');
  if (change.after.length > MISSION_TASKS_MAX_COUNT)
    return missionDetailText('tasks.tooMany');
  return null;
};
